/**
 * ═══════════════════════════════════════════════════════════════════════════
 * PLAN PURCHASE SERVICE - Purchase Validation & Warnings
 * ═══════════════════════════════════════════════════════════════════════════
 * 
 * This service handles plan purchase validations and warnings in the frontend.
 * It checks for upgrade/downgrade scenarios and warns users about potential issues.
 * 
 * IMPORTANT: This is FRONTEND validation only (for UX).
 * Backend MUST also validate to prevent API bypass.
 * 
 * @author MyTamilKart Development Team
 * @version 1.0
 */

import api from '../api/axiosInstance';
import planService from './planService';
import { countOwnProducts } from '../utils/productCount';
import { PLAN_HIERARCHY } from './planService';

// ═══════════════════════════════════════════════════════════════════════════
// PLAN HIERARCHY - Imported from planService (Single Source of Truth)
// ═══════════════════════════════════════════════════════════════════════════
// Now using centralized PLAN_HIERARCHY from planService.js
// Any changes to plan hierarchy should be made in planService.js only

const planPurchaseService = {

  /**
   * Validate plan purchase before proceeding
   * ------------------------------------------
   * Checks current plan and validates the purchase scenario.
   * 
   * @param {string} newPlanId - Plan ID user wants to purchase
   * @param {Object} currentPlanInfo - Current plan information
   * @param {number} currentProductCount - Current number of products
   * @param {number} currentDomainCount - Current number of domains
   * @param {Object} [options]
   * @param {string} [options.newPlanName] - The plan's name from Plan Management, for messages and limits
   * @param {Function} [options.comparePlans] - (aId, bId) => <0 / 0 / >0, the plans page's ranking.
   *   Plan IDs from Plan Management (e.g. "YEARLY STARTER PLAN") aren't PLAN_HIERARCHY keys,
   *   which would rank them level 0 and every purchase as a downgrade (issue #286)
   * @param {number} [options.validityDays] - The plan's validity, for the renewal message (default 365)
   * @returns {Object} Validation result with warnings
   *
   * The product and domain limits are checked for every purchase but a renewal,
   * whatever the ranking: the ranking can be 0 (a current plan that isn't in the
   * list) or put a smaller plan above a bigger one (by price), and the API
   * doesn't check the limits when it activates a plan (eventseasyadmin #287 review)
   *
   * RETURNS:
   * {
   *   allowed: boolean,
   *   scenario: string ('renewal'|'upgrade'|'downgrade'|'change'|'new'),
   *   warnings: string[],
   *   blockers: string[],
   *   confirmationRequired: boolean,
   *   message: string
   * }
   */
  async validatePurchase(newPlanId, currentPlanInfo, currentProductCount = 0, currentDomainCount = 0, options = {}) {
    const newPlanName = options.newPlanName || newPlanId;
    const currentPlanName = currentPlanInfo?.plan_name || currentPlanInfo?.plan_id;
    // Limits by plan ID or name, never the read-only 0-product limits of an
    // unknown ID (issue #286)
    const limitsOf = (planId, planName) => planService.getActivePlanLimits(planId, planName);
    const result = {
      allowed: true,
      scenario: 'new',
      warnings: [],
      blockers: [],
      confirmationRequired: false,
      message: ''
    };
    const daysLeftOnCurrent = () => {
      if (!currentPlanInfo?.expires_on) return 0;
      return Math.ceil((new Date(currentPlanInfo.expires_on) - new Date()) / (1000 * 60 * 60 * 24));
    };

    const currentPlanId = currentPlanInfo?.plan_id;
    const hasCurrentPlan = !!(currentPlanInfo && currentPlanId && !currentPlanInfo.isFreeTrial);

    // SCENARIO: Same plan (renewal): nothing changes but the expiry date
    if (hasCurrentPlan && currentPlanId === newPlanId) {
      const validityDays = options.validityDays || 365;
      result.scenario = 'renewal';
      result.message = `Renewing ${newPlanName} plan for another ${validityDays} days.`;
      const daysLeft = daysLeftOnCurrent();
      if (daysLeft > 0) {
        result.warnings.push(`You have ${daysLeft} days left on your current plan. Your plan will be extended by ${validityDays} more days.`);
      }
      return result;
    }

    // Every other purchase: the seller's products and domains must fit the new plan
    const newLimits = limitsOf(newPlanId, newPlanName);
    // -1: unlimited
    if (newLimits.maxProducts !== -1 && currentProductCount > newLimits.maxProducts) {
      result.allowed = false;
      result.blockers.push(
        `❌ BLOCKED: You have ${currentProductCount} products but ${newPlanName} allows only ${newLimits.maxProducts}.`
      );
      result.blockers.push(
        `   Action required: Delete ${currentProductCount - newLimits.maxProducts} products before switching to ${newPlanName}.`
      );
    }
    if (newLimits.maxDomains !== -1 && currentDomainCount > newLimits.maxDomains) {
      result.allowed = false;
      result.blockers.push(
        `❌ BLOCKED: You have ${currentDomainCount} domains but ${newPlanName} allows only ${newLimits.maxDomains}.`
      );
      result.blockers.push(
        `   Action required: Remove ${currentDomainCount - newLimits.maxDomains} custom domains before switching to ${newPlanName}.`
      );
    }

    // SCENARIO: No current plan (new purchase)
    if (!hasCurrentPlan) {
      result.scenario = 'new';
      result.message = `You're purchasing ${newPlanName} plan. You'll get full access immediately.`;
      return result;
    }

    const currentLimits = limitsOf(currentPlanId, currentPlanInfo.plan_name);
    const ranking = options.comparePlans
      ? options.comparePlans(newPlanId, currentPlanId)
      : (PLAN_HIERARCHY[newPlanId] || 0) - (PLAN_HIERARCHY[currentPlanId] || 0);
    const daysLeft = daysLeftOnCurrent();
    const unlimitedOr = (n) => (n === -1 ? 'Unlimited' : n);

    // SCENARIO: Upgrade (moving to higher plan)
    if (ranking > 0) {
      result.scenario = 'upgrade';
      result.message = `Upgrading from ${currentPlanName} to ${newPlanName}. You'll get better features immediately!`;
      if (daysLeft > 0) {
        result.warnings.push(`⚠️ You have ${daysLeft} days left on ${currentPlanName}. These days will be lost (no refund).`);
        result.confirmationRequired = true;
      }

      // Show what they'll gain
      const gains = [];
      if (newLimits.maxProducts === -1 ? currentLimits.maxProducts !== -1 : newLimits.maxProducts > currentLimits.maxProducts) {
        gains.push(`✅ Products: ${unlimitedOr(currentLimits.maxProducts)} → ${unlimitedOr(newLimits.maxProducts)}`);
      }
      if (newLimits.maxDomains === -1 ? currentLimits.maxDomains !== -1 : newLimits.maxDomains > currentLimits.maxDomains) {
        gains.push(`✅ Domains: ${unlimitedOr(currentLimits.maxDomains)} → ${unlimitedOr(newLimits.maxDomains)}`);
      }
      if (newLimits.analytics !== currentLimits.analytics) {
        gains.push(`✅ Analytics: ${currentLimits.analytics} → ${newLimits.analytics}`);
      }
      if (newLimits.discountCodes && !currentLimits.discountCodes) {
        gains.push(`✅ Discount codes enabled`);
      }
      result.gains = gains;
      return result;
    }

    // SCENARIO: Downgrade (ranking < 0), or a different plan that can't be
    // ranked against the current one (ranking 0, e.g. a legacy plan no longer
    // listed): either way the seller confirms, and sees what they lose
    result.confirmationRequired = true;
    if (ranking < 0) {
      result.scenario = 'downgrade';
      result.message = `⚠️ Downgrading from ${currentPlanName} to ${newPlanName}. You'll lose some features.`;
    } else {
      result.scenario = 'change';
      result.message = `⚠️ Switching from ${currentPlanName} to ${newPlanName}. Check what changes before you continue.`;
    }

    const productLimitDrops = newLimits.maxProducts !== -1
      && (currentLimits.maxProducts === -1 || newLimits.maxProducts < currentLimits.maxProducts);
    if (result.allowed && currentProductCount > 0 && productLimitDrops) {
      result.warnings.push(
        `⚠️ Product limit will reduce: ${unlimitedOr(currentLimits.maxProducts)} → ${newLimits.maxProducts} (You have ${currentProductCount})`
      );
    }

    // Warn about lost features
    const losses = [];
    if (currentLimits.discountCodes && !newLimits.discountCodes) {
      losses.push(`❌ Discount codes will be disabled`);
    }
    if (currentLimits.analytics === 'premium' && newLimits.analytics !== 'premium') {
      losses.push(`❌ Premium analytics will be lost`);
    }
    if (currentLimits.analytics === 'advanced' && newLimits.analytics === 'basic') {
      losses.push(`❌ Advanced analytics will be lost`);
    }
    if (currentLimits.multiStore && !newLimits.multiStore) {
      losses.push(`❌ Multi-store management will be disabled`);
    }
    result.losses = losses;

    if (daysLeft > 0) {
      result.warnings.push(`⚠️ You have ${daysLeft} days left on ${currentPlanName}. These days will be lost.`);
    }
    return result;
  },

  /**
   * Get current product count
   * --------------------------
   * Fetches the current number of products for validation.
   * 
   * @param {string} businessId - Business user ID
   * @returns {Promise<number>} Product count
   */
  async getCurrentProductCount(businessId) {
    try {
      const response = await api.get('/gift/list', { params: { business_id: businessId } });
      if (response.data.status === 'success') {
        // The untouched sample product doesn't take a plan slot (same as the API)
        return countOwnProducts(response.data.result);
      }
      return 0;
    } catch (error) {
      console.error('[PURCHASE SERVICE] Error fetching product count:', error);
      return 0;
    }
  },

  /**
   * Get current domain count
   * -------------------------
   * Fetches the current number of domains for validation.
   * 
   * @param {string} businessId - Business user ID
   * @returns {Promise<number>} Domain count
   */
  async getCurrentDomainCount(businessId) {
    try {
      const response = await api.get(`/business/domain/info?user_id=${businessId}`);
      if (response.data.status === 'success' && response.data.result) {
        return response.data.result.custom_domain ? 1 : 0;
      }
      return 0;
    } catch (error) {
      console.error('[PURCHASE SERVICE] Error fetching domain count:', error);
      return 0;
    }
  },

  /**
   * Format validation result for display
   * -------------------------------------
   * Converts validation result into user-friendly messages.
   * 
   * @param {Object} validation - Validation result
   * @returns {Object} Formatted messages for UI
   */
  formatValidationMessages(validation) {
    const messages = {
      title: '',
      description: '',
      warnings: validation.warnings || [],
      blockers: validation.blockers || [],
      gains: validation.gains || [],
      losses: validation.losses || [],
      canProceed: validation.allowed,
      needsConfirmation: validation.confirmationRequired
    };

    switch (validation.scenario) {
      case 'renewal':
        messages.title = '🔄 Plan Renewal';
        messages.description = validation.message;
        break;
      
      case 'upgrade':
        messages.title = '⬆️ Plan Upgrade';
        messages.description = validation.message;
        break;
      
      case 'downgrade':
        messages.title = '⬇️ Plan Downgrade';
        messages.description = validation.message;
        break;
      
      case 'change':
        messages.title = '🔁 Plan Change';
        messages.description = validation.message;
        break;

      case 'new':
        messages.title = '🎉 New Plan Purchase';
        messages.description = validation.message;
        break;
    }

    return messages;
  }
};

export default planPurchaseService;
